import {
  addAccountAccess,
  fetchAssignableAccounts,
  removeAccountAccess,
  restoreAccountAccess,
} from './accountAccessService';

const respondWith = ({ ok = true, status = 200, body = {} }) => {
  global.fetch = jest
    .fn()
    .mockResolvedValue({ ok, status, json: async () => body });
};

beforeEach(() => {
  document.head.innerHTML = '<meta name="csrf-token" content="a-token" />';
});

afterEach(() => {
  document.head.innerHTML = '';
  delete global.fetch;
});

describe('management/EditUser accountAccessService', () => {
  describe('removeAccountAccess', () => {
    it('posts to the remove path with the account id', async () => {
      respondWith({ body: { accountAccess: [] } });

      await removeAccountAccess(7, 101);

      expect(global.fetch).toHaveBeenCalledWith(
        '/management/users/7/remove_account_access',
        expect.objectContaining({
          method: 'POST',
          body: JSON.stringify({ account_id: 101 }),
        }),
      );
    });

    it('sends the CSRF token from the page', async () => {
      respondWith({ body: { accountAccess: [] } });

      await removeAccountAccess(7, 101);

      const { headers } = global.fetch.mock.calls[0][1];
      expect(headers['X-CSRF-Token']).toBe('a-token');
    });

    it('returns the refreshed accountAccess on success', async () => {
      const accountAccess = [{ id: 101, removedAt: '2025-01-01T00:00:00Z' }];
      respondWith({ body: { accountAccess } });

      await expect(removeAccountAccess(7, 101)).resolves.toEqual({
        accountAccess,
      });
    });

    it('returns the base error when blocked rather than throwing', async () => {
      respondWith({
        ok: false,
        status: 422,
        body: {
          errors: {
            base: [
              'Something went wrong removing this account. Please try again.',
            ],
          },
        },
      });

      await expect(removeAccountAccess(7, 101)).resolves.toEqual({
        errors: {
          base: [
            'Something went wrong removing this account. Please try again.',
          ],
        },
        terminal: false,
      });
    });

    it('carries the terminal flag when the server sets it', async () => {
      respondWith({
        ok: false,
        status: 422,
        body: {
          errors: {
            base: ['This user must have access to at least one account.'],
          },
          terminal: true,
        },
      });

      await expect(removeAccountAccess(7, 101)).resolves.toEqual({
        errors: {
          base: ['This user must have access to at least one account.'],
        },
        terminal: true,
      });
    });

    it('throws on a failure carrying no messages', async () => {
      respondWith({ ok: false, status: 500, body: {} });

      await expect(removeAccountAccess(7, 101)).rejects.toThrow('status: 500');
    });
  });

  describe('restoreAccountAccess', () => {
    it('posts to the restore path with the account id', async () => {
      respondWith({ body: { accountAccess: [] } });

      await restoreAccountAccess(7, 101);

      expect(global.fetch).toHaveBeenCalledWith(
        '/management/users/7/restore_account_access',
        expect.objectContaining({
          method: 'POST',
          body: JSON.stringify({ account_id: 101 }),
        }),
      );
    });

    it('returns the refreshed accountAccess on success', async () => {
      const accountAccess = [{ id: 101, removedAt: null }];
      respondWith({ body: { accountAccess } });

      await expect(restoreAccountAccess(7, 101)).resolves.toEqual({
        accountAccess,
      });
    });

    it('throws on a failure carrying no messages', async () => {
      respondWith({ ok: false, status: 500, body: {} });

      await expect(restoreAccountAccess(7, 101)).rejects.toThrow('status: 500');
    });
  });

  describe('addAccountAccess', () => {
    it('posts to the add path with the account ids', async () => {
      respondWith({ body: { accountAccess: [] } });

      await addAccountAccess(7, [101, 102]);

      expect(global.fetch).toHaveBeenCalledWith(
        '/management/users/7/add_account_access',
        expect.objectContaining({
          method: 'POST',
          body: JSON.stringify({ account_ids: [101, 102] }),
        }),
      );
    });

    it('sends the CSRF token from the page', async () => {
      respondWith({ body: { accountAccess: [] } });

      await addAccountAccess(7, [101]);

      const { headers } = global.fetch.mock.calls[0][1];
      expect(headers['X-CSRF-Token']).toBe('a-token');
    });

    it('returns the refreshed accountAccess on success', async () => {
      const accountAccess = [{ id: 101, removedAt: null }];
      respondWith({ body: { accountAccess } });

      await expect(addAccountAccess(7, [101])).resolves.toEqual({
        accountAccess,
      });
    });

    it('returns the base error when it fails rather than throwing', async () => {
      respondWith({
        ok: false,
        status: 422,
        body: { errors: { base: ['Choose at least one account to add.'] } },
      });

      await expect(addAccountAccess(7, [])).resolves.toEqual({
        errors: { base: ['Choose at least one account to add.'] },
        terminal: false,
      });
    });

    it('throws on a failure carrying no messages', async () => {
      respondWith({ ok: false, status: 500, body: {} });

      await expect(addAccountAccess(7, [101])).rejects.toThrow('status: 500');
    });
  });

  describe('fetchAssignableAccounts', () => {
    it('fetches the assignable_accounts path as JSON', async () => {
      respondWith({ body: { accounts: [], total: 0 } });

      await fetchAssignableAccounts(7);

      expect(global.fetch).toHaveBeenCalledWith(
        '/management/users/7/assignable_accounts.json',
      );
    });

    it('carries a search query as a q param', async () => {
      respondWith({ body: { accounts: [], total: 0 } });

      await fetchAssignableAccounts(7, 'beta');

      expect(global.fetch).toHaveBeenCalledWith(
        '/management/users/7/assignable_accounts.json?q=beta',
      );
    });

    it('carries an offset as a param', async () => {
      respondWith({ body: { accounts: [], total: 0 } });

      await fetchAssignableAccounts(7, '', 25);

      expect(global.fetch).toHaveBeenCalledWith(
        '/management/users/7/assignable_accounts.json?offset=25',
      );
    });

    it('returns the accounts and total', async () => {
      const accounts = [{ label: 'Alpha Fleet', value: 9 }];
      respondWith({ body: { accounts, total: 3 } });

      await expect(fetchAssignableAccounts(7)).resolves.toEqual({
        accounts,
        total: 3,
      });
    });

    it('falls back to the loaded count when total is missing', async () => {
      const accounts = [{ label: 'Alpha Fleet', value: 9 }];
      respondWith({ body: { accounts } });

      await expect(fetchAssignableAccounts(7)).resolves.toEqual({
        accounts,
        total: 1,
      });
    });

    it('throws on a failed request', async () => {
      respondWith({ ok: false, status: 500, body: {} });

      await expect(fetchAssignableAccounts(7)).rejects.toThrow('status: 500');
    });
  });
});
